import { useContext, useState } from 'react';
import {
  AppInsightsContext,
  MetadataContext,
  Modal,
  ProgressCircular,
  SystemFeaturesContext,
  Tooltip,
  TrackEventKey,
  TrackEventName,
  TrackEventType,
  useNotification,
  UserContext,
} from '@/components';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import DownloadSimpleLight from '@/assets/downloadSimpleLight.svg';
import { DownloadHubModel } from '@/types';
import XLight from '@/assets/xLight.svg';
import ImagesSquareLight from '@/assets/imagesSquareLight.svg';
import FilePdfLight from '@/assets/filePdfLight.svg';
import FilePptLight from '@/assets/file-ppt-light.svg';
import LecternLight from '@/assets/lectern-light.svg';
import { downloadImages } from '@/api';
import { useRouter } from 'next/router';
import { isValidHttpUrl } from '@/utils';

type DownloadHubButtonModel = {
  data?: DownloadHubModel;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
};

const DownloadHubButton = ({ data, className, tooltipPlacement = 'top' }: DownloadHubButtonModel) => {
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [isPptDownloading, setIsPptDownloading] = useState(false);
  const [isKeyNoteDownloading, setIsKeyNoteDownloading] = useState(false);

  const t = useTranslations();
  const {
    useNewDownloadHub: systemFeaturesUseNewDownloadHub,
    pdfDownload,
    slideDownload,
  } = useContext(SystemFeaturesContext);
  const appInsights = useContext(AppInsightsContext);
  const { pageId, pageType, isPublic } = useContext(MetadataContext);
  const user = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const { asPath } = useRouter();

  const { showNotification } = useNotification();

  const closeModal = () => {
    setOpen(false);
  };

  if (!systemFeaturesUseNewDownloadHub) {
    // return null;
  }

  const downloadImagesGroups = () => {
    if (pageType === 'legacyReportPage' && data?.groupedImages && data.groupedImages.length > 0) {
      downloadImages(data?.groupedImages[0].images);
    } else {
      const imagesList: number[] = [];
      data?.groupedImages.forEach((group) => {
        imagesList.push(...group.images.map((image) => image.mediaId));
      });

      downloadImages(imagesList);
    }
  };

  const downloadFile = async (downloadType: string, linkFile?: string) => {
    let eventKey;

    if (downloadType === 'pdf') {
      eventKey = TrackEventKey.PDF;
      setIsPdfDownloading(true);
    } else if (downloadType === 'ppt') {
      eventKey = TrackEventKey.POWERPOINT;
      setIsPptDownloading(true);
    } else if (downloadType === 'keynote') {
      eventKey = TrackEventKey.KEYNOTE;
      setIsKeyNoteDownloading(true);
    }

    if (eventKey) {
      appInsights.trackCustomEvent(TrackEventName.REPORT, eventKey, TrackEventType.DOWNLOAD, user, {
        pageId,
        pagePath: asPath,
      });
    }

    if (linkFile) {
      let downloadLink = linkFile;
      let filename = '';

      if (isValidHttpUrl(linkFile)) {
        const lastPart = window.location.pathname.split('/').filter(Boolean).pop();
        filename = lastPart + `.${downloadType}`;
      } else {
        downloadLink = `${window.location.origin}${linkFile.startsWith('/') ? '' : '/'}${linkFile}`;
        filename = downloadLink.split('/').filter(Boolean).pop() || `download.${downloadType}`;
      }

      try {
        const endpoint = `/api/download?url=${encodeURIComponent(downloadLink)}&filename=${encodeURIComponent(filename)}`;

        const res = await fetch(endpoint, { method: 'HEAD' });

        if (!res.ok) throw new Error('File unavailable');

        setIsPdfDownloading(false);
        setIsPptDownloading(false);
        setIsKeyNoteDownloading(false);

        const link = document.createElement('a');
        link.href = endpoint;
        link.download = ''; // forces download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error: any) {
        showNotification(`Failed to download file: ${error.message}`, 'error');
      }
    }
  };

  if (!data) {
    return null;
  }

  return (
    <>
      <Tooltip
        placement={tooltipPlacement}
        backgroundColor={!data ? 'orange' : ''}
        content={data ? t('downloadHub.$downloadHubTitle') : t('membershipLevels.$featurenotavailable')}
      >
        <button
          className={classNames(
            'rounded-full p-2 hover:bg-gray-100',
            className,
            data ? 'cursor-pointer' : '!cursor-not-allowed opacity-50',
          )}
          disabled={!data}
          onClick={() => data && setOpen(true)}
        >
          <DownloadSimpleLight className="fill-from-bg h-7 w-7" />
        </button>
      </Tooltip>
      <Modal
        open={open}
        onClose={closeModal}
        contanerClassName="w-auto h-auto bg-white !rounded-none lg:!rounded-md disable-scroll"
      >
        <div className="flex min-w-[330px] flex-col gap-4 px-6 py-4">
          <div className="flex flex-row items-center justify-between gap-3">
            <div className="text-h5">{t('downloadHub.$downloadHubTitle')}</div>
            <button className="btn icon" onClick={closeModal}>
              <XLight className="h-7 w-7" />
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {data.groupedImages && data.groupedImages.length > 0 && (
              <div className="flex flex-row items-center justify-between gap-2">
                <div className="flex flex-row gap-5">
                  <ImagesSquareLight className="h-7 w-7" />
                  <div className="text-sm text-black/60">{t('downloadHub.$downloadHubImages')}</div>
                </div>
                <button className="btn icon" onClick={downloadImagesGroups}>
                  <DownloadSimpleLight className="h-7 w-7" />
                </button>
              </div>
            )}
            <Tooltip
              placement="top"
              backgroundColor="orange"
              inactive={pdfDownload || isPublic}
              content={t('membershipLevels.$featurenotavailable')}
            >
              <div className="flex flex-row items-center justify-between gap-2">
                <div className="flex flex-row gap-5">
                  <FilePdfLight className="h-7 w-7" />
                  <div className="text-sm text-black/60">{t('downloadHub.$downloadHubPdf')}</div>
                </div>
                <button
                  disabled={(!pdfDownload || !data?.downloadPdfLink) && (!isPublic || !data?.downloadPdfLink)}
                  className="btn icon"
                  onClick={() => downloadFile('pdf', data?.downloadPdfLink?.href)}
                >
                  {pdfDownload && (isPdfDownloading || !data?.downloadPdfLink) ? (
                    <ProgressCircular indeterminate size={28} width={3} colorClassName="stroke-black/80" />
                  ) : (
                    <DownloadSimpleLight className="h-7 w-7" />
                  )}
                </button>
              </div>
            </Tooltip>
            {data.downloadPowerPointLink && (
              <Tooltip
                placement="top"
                backgroundColor="orange"
                inactive={slideDownload || isPublic}
                content={t('membershipLevels.$featurenotavailable')}
              >
                <div className="flex flex-row items-center justify-between gap-2">
                  <div className="flex flex-row gap-5">
                    <FilePptLight className="h-7 w-7" />
                    <div className="text-sm text-black/60">{t('downloadHub.$downloadHubPowerPoint')}</div>
                  </div>
                  <button
                    disabled={!slideDownload && !isPublic}
                    className="btn icon"
                    onClick={() => downloadFile('ppt', data.downloadPowerPointLink?.href)}
                  >
                    {isPptDownloading ? (
                      <ProgressCircular indeterminate size={28} width={3} colorClassName="stroke-black/80" />
                    ) : (
                      <DownloadSimpleLight className="h-7 w-7" />
                    )}
                  </button>
                </div>
              </Tooltip>
            )}
            {data.downloadKeyNoteLink && (
              <Tooltip
                placement="top"
                backgroundColor="orange"
                inactive={slideDownload || isPublic}
                content={t('membershipLevels.$featurenotavailable')}
              >
                <div className="flex flex-row items-center justify-between gap-2">
                  <div className="flex flex-row gap-5">
                    <LecternLight className="h-7 w-7" />
                    <div className="text-sm text-black/60">{t('downloadHub.$downloadHubKeynote')}</div>
                  </div>
                  <button
                    disabled={!slideDownload && !isPublic}
                    className="btn icon"
                    onClick={() => downloadFile('keynote', data.downloadKeyNoteLink?.href)}
                  >
                    {isKeyNoteDownloading ? (
                      <ProgressCircular indeterminate size={28} width={3} colorClassName="stroke-black/80" />
                    ) : (
                      <DownloadSimpleLight className="h-7 w-7" />
                    )}
                  </button>
                </div>
              </Tooltip>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DownloadHubButton;
